import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Sparkles, Plus, Play, Loader2, Lock, X, ArrowRight,
    CheckCircle2, AlertTriangle, Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { readSseLines } from "@/lib/sse";

/* ================================================================
   Nexus One · WorkerPage side-widget — Phase 5-A custom skills installer
   D1 reskin — dark theme + Workforce Member terminology.
   ================================================================ */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomSkillsPanel({ hiredId, workerName, workerRole }) {
    const [installed, setInstalled] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [available, setAvailable] = useState([]);
    const [runOpen, setRunOpen] = useState(null);
    const [running, setRunning] = useState(false);
    const [runInputs, setRunInputs] = useState({});
    const [runOutput, setRunOutput] = useState("");
    const [awaitingApproval, setAwaitingApproval] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/workers/hired/${hiredId}/custom-skills`);
            setInstalled(data.skills || []);
            setLoadError("");
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Could not load custom skills.";
            setLoadError(typeof detail === "string" ? detail : "Could not load custom skills.");
            setInstalled([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [hiredId]);

    const openPicker = async () => {
        setPickerOpen(true);
        try {
            const [myR, marketR] = await Promise.all([
                api.get("/custom-skills", { params: { tab: "my" } }),
                api.get("/custom-skills", { params: { tab: "marketplace" } }),
            ]);
            const alreadyIds = new Set(installed.map((s) => s.id));
            const merged = [
                ...(myR.data.skills || []).filter((s) => s.status === "active"),
                ...(marketR.data.skills || []),
            ].filter((s, i, arr) => arr.findIndex((x) => x.id === s.id) === i && !alreadyIds.has(s.id));
            setAvailable(merged);
        } catch (e) {
            toast.error("Could not load available skills.");
        }
    };

    const install = async (skillId) => {
        try {
            await api.post(`/workers/hired/${hiredId}/install-custom-skill`, { custom_skill_id: skillId });
            toast.success("Skill installed.");
            setPickerOpen(false);
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            if (d && typeof d === "object" && d.error === "skill_eligibility_mismatch") {
                toast.error(d.message);
            } else {
                toast.error(typeof d === "string" ? d : "Install failed.");
            }
        }
    };

    const uninstall = async (skillId) => {
        try {
            await api.delete(`/workers/hired/${hiredId}/custom-skills/${skillId}`);
            toast.success("Skill uninstalled.");
            load();
        } catch { toast.error("Uninstall failed."); }
    };

    const openRun = (skill) => {
        setRunOpen(skill);
        setRunOutput("");
        setAwaitingApproval(null);
        const init = {};
        (skill.required_inputs || []).forEach((i) => { init[i.key] = ""; });
        setRunInputs(init);
    };

    const executeRun = async () => {
        if (!runOpen) return;
        for (const inp of runOpen.required_inputs || []) {
            if (inp.required !== false && !String(runInputs[inp.key] || "").trim()) {
                toast.error(`Missing required input: ${inp.label || inp.key}`);
                return;
            }
        }
        setRunning(true);
        setRunOutput("");
        setAwaitingApproval(null);
        try {
            const token = localStorage.getItem("wfos_token");
            const resp = await fetch(`${API_BASE}/workers/hired/${hiredId}/run-skill`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ custom_skill_id: runOpen.id, inputs: runInputs }),
            });
            if (!resp.ok) {
                const text = await resp.text();
                let detail = text;
                try { detail = JSON.parse(text).detail; } catch {}
                throw new Error(typeof detail === "string" ? detail : "Run failed.");
            }
            const ct = resp.headers.get("content-type") || "";
            if (ct.includes("application/json")) {
                const j = await resp.json();
                if (j.status === "awaiting_approval") {
                    setAwaitingApproval(j);
                    toast.info(j.message || "Skill queued for approval.");
                } else {
                    setRunOutput(j.output || JSON.stringify(j, null, 2));
                }
                return;
            }
            let acc = "";
            for await (const j of readSseLines(resp)) {
                if (j.type === "delta" && j.content) {
                    acc += j.content;
                    setRunOutput(acc);
                } else if (j.type === "done" && j.output) {
                    acc = j.output;
                    setRunOutput(acc);
                } else if (j.type === "error") {
                    toast.error(j.message || "Run failed.");
                }
            }
        } catch (e) {
            toast.error(e.message || "Run failed.");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="mt-4" data-testid="worker-custom-skills-panel">
            <div className="flex items-center justify-between">
                <div className="atlas-label">Custom skills</div>
                <button
                    onClick={openPicker}
                    data-testid="worker-install-skill"
                    className="inline-flex items-center gap-1 text-[11.5px] font-medium"
                    style={{ color: "var(--atlas-primary-hover)" }}
                >
                    <Plus className="w-3 h-3" strokeWidth={2} /> Install
                </button>
            </div>

            {loadError && (
                <div
                    className="mt-2 rounded-md px-2 py-1.5 text-[11.5px]"
                    style={{
                        background: "var(--atlas-danger-muted)",
                        border: "1px solid rgba(239,68,68,0.35)",
                        color: "#fca5a5",
                    }}
                    data-testid="worker-skills-error"
                >
                    {loadError}
                </div>
            )}
            {loading ? (
                <div className="mt-2 text-[12px]" style={{ color: "var(--atlas-text-muted)" }}>Loading…</div>
            ) : !loadError && installed.length === 0 ? (
                <div className="mt-2 text-[12px] italic" style={{ color: "var(--atlas-text-muted)" }}>
                    No custom skills installed.{" "}
                    <Link
                        to="/app/skills"
                        style={{ color: "var(--atlas-primary-hover)" }}
                        className="hover:underline"
                    >
                        Browse →
                    </Link>
                </div>
            ) : (
                <ul className="mt-2 space-y-1.5" data-testid="worker-installed-skills">
                    {installed.map((s) => (
                        <li
                            key={s.id}
                            className="rounded-md p-2"
                            style={{
                                background: "var(--atlas-bg-elevated)",
                                border: "1px solid var(--atlas-border)",
                            }}
                            data-testid={`worker-skill-${s.slug}`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <div
                                        className="text-[12.5px] font-medium truncate flex items-center gap-1"
                                        style={{ color: "var(--atlas-text-primary)" }}
                                    >
                                        {s.icon && <span>{s.icon}</span>}
                                        {s.name}
                                        {s.approval_required && (
                                            <Lock className="w-2.5 h-2.5" strokeWidth={2.5} style={{ color: "#fbbf24" }} />
                                        )}
                                    </div>
                                    <div
                                        className="text-[10.5px] truncate"
                                        style={{ color: "var(--atlas-text-muted)" }}
                                    >
                                        {s.category} · v{s.version}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => openRun(s)}
                                        data-testid={`worker-run-skill-${s.slug}`}
                                        className="atlas-btn-primary inline-flex items-center gap-1 rounded-md px-2 h-6 text-[11px]"
                                    >
                                        <Play className="w-2.5 h-2.5" strokeWidth={2.5} /> Run
                                    </button>
                                    <button
                                        onClick={() => uninstall(s.id)}
                                        title="Uninstall"
                                        data-testid={`worker-uninstall-skill-${s.slug}`}
                                        style={{ color: "var(--atlas-text-muted)" }}
                                        className="hover:opacity-100 opacity-70 transition-opacity"
                                    >
                                        <Trash2 className="w-3 h-3" strokeWidth={2} />
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* Picker modal */}
            {pickerOpen && (
                <div
                    className="fixed inset-0 z-50 grid place-items-center p-6"
                    style={{ background: "rgba(2,4,10,0.72)", backdropFilter: "blur(4px)" }}
                    onClick={() => setPickerOpen(false)}
                >
                    <div
                        className="atlas-card w-full max-w-lg p-5 max-h-[80vh] overflow-y-auto atlas-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="worker-skill-picker"
                    >
                        <div className="flex items-center justify-between">
                            <div
                                className="text-[15px] font-semibold"
                                style={{ color: "var(--atlas-text-primary)" }}
                            >
                                Install a custom skill
                            </div>
                            <button
                                onClick={() => setPickerOpen(false)}
                                style={{ color: "var(--atlas-text-muted)" }}
                                className="hover:text-white transition-colors"
                            >
                                <X className="w-4 h-4" strokeWidth={2} />
                            </button>
                        </div>
                        <div className="mt-1 text-[12px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Skills eligible for <b>{workerName}</b> ({workerRole}).
                        </div>
                        <div className="mt-4 space-y-2">
                            {available.length === 0 ? (
                                <div className="text-[13px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    No new skills available.{" "}
                                    <Link
                                        to="/app/skills/new"
                                        style={{ color: "var(--atlas-primary-hover)" }}
                                        className="hover:underline"
                                    >
                                        Build one →
                                    </Link>
                                </div>
                            ) : available.map((s) => (
                                <div
                                    key={s.id}
                                    className="rounded-lg p-3 flex items-start justify-between gap-3"
                                    style={{
                                        background: "var(--atlas-bg-elevated)",
                                        border: "1px solid var(--atlas-border-strong)",
                                    }}
                                    data-testid={`skill-picker-row-${s.slug}`}
                                >
                                    <div className="min-w-0">
                                        <div
                                            className="text-[13px] font-medium flex items-center gap-1.5"
                                            style={{ color: "var(--atlas-text-primary)" }}
                                        >
                                            {s.icon && <span>{s.icon}</span>}
                                            {s.name}
                                            {s.approval_required && (
                                                <span
                                                    className="text-[10px] inline-flex items-center gap-0.5"
                                                    style={{ color: "#fbbf24" }}
                                                >
                                                    <Lock className="w-2.5 h-2.5" strokeWidth={2.5} /> {s.approval_role}
                                                </span>
                                            )}
                                        </div>
                                        <div
                                            className="mt-0.5 text-[11.5px] line-clamp-2"
                                            style={{ color: "var(--atlas-text-tertiary)" }}
                                        >
                                            {s.description}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => install(s.id)}
                                        data-testid={`skill-picker-install-${s.slug}`}
                                        className="atlas-btn-primary inline-flex items-center gap-1 rounded-md px-2.5 h-7 text-[11.5px] flex-shrink-0"
                                    >
                                        Install <ArrowRight className="w-3 h-3" strokeWidth={2.5} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Run modal */}
            {runOpen && (
                <div
                    className="fixed inset-0 z-50 grid place-items-center p-6"
                    style={{ background: "rgba(2,4,10,0.72)", backdropFilter: "blur(4px)" }}
                    onClick={() => !running && setRunOpen(null)}
                >
                    <div
                        className="atlas-card w-full max-w-2xl p-5 max-h-[85vh] overflow-y-auto atlas-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="worker-run-modal"
                    >
                        <div className="flex items-center justify-between">
                            <div className="min-w-0">
                                <div
                                    className="text-[15px] font-semibold flex items-center gap-1.5"
                                    style={{ color: "var(--atlas-text-primary)" }}
                                >
                                    <Sparkles
                                        className="w-4 h-4"
                                        strokeWidth={2}
                                        style={{ color: "var(--atlas-primary-hover)" }}
                                    />
                                    Run {runOpen.name}
                                </div>
                                <div className="mt-0.5 text-[12px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    {runOpen.description}
                                </div>
                            </div>
                            <button
                                onClick={() => !running && setRunOpen(null)}
                                disabled={running}
                                style={{ color: "var(--atlas-text-muted)" }}
                                className="hover:text-white transition-colors disabled:opacity-40"
                            >
                                <X className="w-4 h-4" strokeWidth={2} />
                            </button>
                        </div>

                        <div className="mt-4 space-y-3">
                            {(runOpen.required_inputs || []).map((inp) => (
                                <div key={inp.key}>
                                    <label
                                        className="block text-[12.5px] font-medium mb-1"
                                        style={{ color: "var(--atlas-text-secondary)" }}
                                    >
                                        {inp.label || inp.key}{" "}
                                        {inp.required !== false && <span style={{ color: "#fca5a5" }}>*</span>}
                                    </label>
                                    {inp.type === "text" ? (
                                        <textarea
                                            rows={3}
                                            value={runInputs[inp.key] || ""}
                                            onChange={(e) => setRunInputs({ ...runInputs, [inp.key]: e.target.value })}
                                            data-testid={`worker-run-input-${inp.key}`}
                                            className="w-full rounded-lg p-2 text-[13px] resize-y"
                                        />
                                    ) : inp.type === "select" ? (
                                        <select
                                            value={runInputs[inp.key] || ""}
                                            onChange={(e) => setRunInputs({ ...runInputs, [inp.key]: e.target.value })}
                                            data-testid={`worker-run-input-${inp.key}`}
                                            className="w-full rounded-lg h-10 px-3 text-[13.5px]"
                                        >
                                            <option value="">—</option>
                                            {(inp.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : (
                                        <input
                                            type={inp.type === "number" ? "number" : (inp.type === "date" ? "date" : "text")}
                                            value={runInputs[inp.key] || ""}
                                            onChange={(e) => setRunInputs({ ...runInputs, [inp.key]: e.target.value })}
                                            data-testid={`worker-run-input-${inp.key}`}
                                            className="w-full rounded-lg h-10 px-3 text-[13.5px]"
                                        />
                                    )}
                                    {inp.help_text && (
                                        <div className="mt-0.5 text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>
                                            {inp.help_text}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 flex items-center gap-2">
                            <button
                                onClick={executeRun}
                                disabled={running}
                                data-testid="worker-run-execute"
                                className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-10 text-[13px]"
                            >
                                {running ? (
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                                ) : (
                                    <Play className="w-4 h-4" strokeWidth={2} />
                                )}
                                {running ? "Running…" : "Run skill"}
                            </button>
                            {runOpen.approval_required && (
                                <span
                                    className="text-[11.5px] inline-flex items-center gap-1"
                                    style={{ color: "#fbbf24" }}
                                >
                                    <Lock className="w-3 h-3" strokeWidth={2.5} /> Requires {runOpen.approval_role} approval.
                                </span>
                            )}
                        </div>

                        {awaitingApproval && (
                            <div
                                className="mt-4 rounded-lg p-3 text-[12.5px]"
                                style={{
                                    background: "var(--atlas-warning-muted)",
                                    border: "1px solid rgba(245,158,11,0.45)",
                                    color: "#fbbf24",
                                }}
                                data-testid="worker-run-awaiting-approval"
                            >
                                <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={2} />
                                {awaitingApproval.message}
                            </div>
                        )}

                        {runOutput && (
                            <div className="mt-4" data-testid="worker-run-output">
                                <div
                                    className="text-[11px] font-semibold uppercase tracking-widest mb-1.5 flex items-center gap-1"
                                    style={{ color: "var(--atlas-text-muted)" }}
                                >
                                    <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} style={{ color: "#34d399" }} />
                                    Output
                                </div>
                                <div
                                    className="whitespace-pre-wrap text-[13px] leading-relaxed rounded-lg p-3 max-h-[380px] overflow-y-auto atlas-scrollbar"
                                    style={{
                                        color: "var(--atlas-text-primary)",
                                        background: "var(--atlas-bg-deep)",
                                        border: "1px solid var(--atlas-border-strong)",
                                    }}
                                >
                                    {runOutput}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
